var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isNumber, uniq, uniqBy } from "lodash";
import { contactSelectInlineV2ValueSelector, } from "../../redux/form/form.utils";
import * as propertySelectorsV2 from "../../reduxV2/property/property.selectors";
import { EContractCostsAreIndexable, EContractIndexationStatus, EContractMemberTypes, EContractPaydate, EContractPaymentRepetitionType, ENoVatType, } from "../../types/contract.types";
import { EPaymentMethod, EPaymentOrderType, EPaymentRepetitionType, } from "../../types/payment.types";
import { EPropertyMemberTypes } from "../../types/property.types";
import { differenceInDays, endOfDay, endOfMonth, endOfQuarter, endOfYear, getDay, getMonth, isAfter, isBefore, max, startOfDay, subDays, } from "../../utils/date-fns.utils";
import { stripTime } from "../../utils/date.utils";
import { getAllRequiredCompanyFields, getAllRequiredContactFields, } from "../../utils/forms.utils";
import { getLocalizedText } from "../../utils/i18n/i18n.utils";
import { formatCurrency } from "../../utils/number.utils";
import { EField as EContactField } from "../addContact/schema.addContact.types";
import { ETypeSelect } from "./schema.createPayment.0";
import { getProRataPeriodDays } from "./schema.createPayment.3";
import { EField, EPayoutReferenceType, RECURRING_COSTS, } from "./schema.createPayment.types";
import { ECreatePaymentMethod, } from "./schema.createPayment.types";
var NOW = new Date();
var YESTERDAY = subDays(new Date(), 1);
// Who can pay a cost payment: the tenants and the parents/guardians.
var COST_PAYER_CONTRACT_ROLES = [
    EContractMemberTypes.Tenant,
    EContractMemberTypes.Parent,
];
var COST_PAYER_PROPERTY_ROLES = [
    EPropertyMemberTypes.Tenant,
    EPropertyMemberTypes.Parent,
];
var RENT_ORDER_TYPES = [
    EPaymentOrderType.Rent,
    EPaymentOrderType.RentAndCosts,
];
// The account of the only member with one of these roles. Undefined when
// there is none, or more than one: then the broker chooses (as for any
// other payment), instead of the form guessing by member order.
var getOnlyMemberAccountId = function (params) {
    var _a = params.members, members = _a === void 0 ? [] : _a, roles = params.roles;
    var accountIds = uniq(members
        .filter(function (member) { return roles.some(function (role) { return member.roles.includes(role); }); })
        .map(function (member) { return member.account.id; }));
    return accountIds.length === 1 ? accountIds[0] : undefined;
};
// The payer of a cost payment when the embedder names none. For a payment on
// a lease: whoever pays that lease's rent (a previous tenant's rent order on
// the same property does not count), else its only tenant or parent. Without
// a lease: the property's rent payer, else the only tenant or parent of the
// property's single lease or of the property. Undefined = ask.
var getCostPaymentPayerId = function (params) {
    var _a, _b;
    var extraData = params.extraData, contract = params.contract, isLinkedToContract = params.isLinkedToContract;
    var rentPaymentOrder = ((_a = extraData.paymentOrders) !== null && _a !== void 0 ? _a : []).find(function (paymentOrder) {
        return RENT_ORDER_TYPES.includes(paymentOrder.type) &&
            (!isLinkedToContract || paymentOrder.contractId === (contract === null || contract === void 0 ? void 0 : contract.id));
    });
    if ((rentPaymentOrder === null || rentPaymentOrder === void 0 ? void 0 : rentPaymentOrder.payerId) != null) {
        return rentPaymentOrder.payerId;
    }
    var contractPayerId = getOnlyMemberAccountId({
        members: contract === null || contract === void 0 ? void 0 : contract.members,
        roles: COST_PAYER_CONTRACT_ROLES,
    });
    if (contractPayerId != null || isLinkedToContract) {
        return contractPayerId;
    }
    return getOnlyMemberAccountId({
        members: (_b = extraData.property) === null || _b === void 0 ? void 0 : _b.members,
        roles: COST_PAYER_PROPERTY_ROLES,
    });
};
// Whether a cost payment asks for its payer: when it is not known up front
// (the embedder names none and the lease does not decide), or when the
// embedder wants it confirmed. Decided on the initial values, so the step
// stays once the broker answered it (and the summary shows the payer).
// Never when every split names its own payer: one asked here bills nobody.
export var shouldAskCostPaymentPayer = function (params) {
    var _a, _b, _c, _d, _e;
    var extraData = params.extraData, initialValues = params.initialValues;
    var costSplits = (_b = (_a = extraData.prefill) === null || _a === void 0 ? void 0 : _a.costSplits) !== null && _b !== void 0 ? _b : [];
    var hasPayerPerSplit = costSplits.length > 1 && costSplits.every(function (split) { return split.payerId != null; });
    if (hasPayerPerSplit) {
        return false;
    }
    var isPayerKnown = ((_d = (_c = initialValues[EField.Payer]) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) > 0;
    return !isPayerKnown || !!((_e = extraData.prefill) === null || _e === void 0 ? void 0 : _e.askPayer);
};
// Whether a cost payment may bill this account: anyone, unless the embedder
// names who (see IPaymentPrefill.payerAccountIds).
export var isAllowedCostPayer = function (params) {
    var payerAccountIds = params.payerAccountIds, accountId = params.accountId;
    return !payerAccountIds || payerAccountIds.includes(Number(accountId));
};
export var isRentOrCost = function (type) {
    return type &&
        [
            EPaymentOrderType.RentAndCosts,
            EPaymentOrderType.Rent,
            EPaymentOrderType.CommonCosts,
            EPaymentOrderType.NonCommonCosts,
        ].includes(type);
};
export var showStartDateAsDropdown = function (formData) {
    // Don't show if not rent or cost
    if (!isRentOrCost(formData[EField.TypeRecurring])) {
        return false;
    }
    return true;
};
export var checkIfTypeIsAdditionalCost = function (formData) { var _a; return (_a = formData[EField.TypeRecurring]) === null || _a === void 0 ? void 0 : _a.includes(EPaymentOrderType.AdditionalCosts); };
export var getSelectedAdditionalCost = function (_a) {
    var contract = _a.contract, formData = _a.formData;
    if (contract === null || contract === void 0 ? void 0 : contract.additionalCosts) {
        var index = formData[EField.TypeRecurring].split("_")[2];
        return contract.additionalCosts[index];
    }
    return;
};
export var checkIfTypeIsOther = function (formData) {
    var isTypeOtherRecurring = formData[EField.TypeRecurring] === EPaymentOrderType.OtherRecurring;
    var isTypeOtherOneoff = formData[EField.TypeOnce] === EPaymentOrderType.OtherOneoff;
    var isTypeAdditionalCost = checkIfTypeIsAdditionalCost(formData);
    return isTypeOtherRecurring || isTypeOtherOneoff || isTypeAdditionalCost;
};
export var shouldAskOwnerPaymentMethod = function (formData, extraData) {
    var _a;
    // TODO: Can be other account ID
    var payerAccountId = (_a = formData[EField.Payer]) === null || _a === void 0 ? void 0 : _a[0];
    var contract = getRelatedContract(extraData, formData);
    if (contract && payerAccountId) {
        var payerMember = contract.members.find(function (member) { return member.account.id === payerAccountId; });
        var isOtherRecurring = formData[EField.TypeRecurring] === EPaymentOrderType.OtherRecurring;
        var isDifferentPeriodFromContract = formData[EField.PaymentRepetition] !== contract.repetitionType;
        return (!(isOtherRecurring && isDifferentPeriodFromContract) &&
            !!(payerMember === null || payerMember === void 0 ? void 0 : payerMember.roles.includes(EContractMemberTypes.Owner)));
    }
    return false;
};
export var getStartDate = function (formData) {
    if (showStartDateAsDropdown(formData)) {
        if (formData[EField.StartDateDropdown]) {
            return new Date(formData[EField.StartDateDropdown]);
        }
        return undefined;
    }
    if (formData[EField.StartDate]) {
        return formData[EField.StartDate];
    }
    return undefined;
};
export var isRecurringCostDisabled = function (costType, isTenant, contract, internalModeEnabled) {
    if (isTenant === void 0) { isTenant = false; }
    if (!contract) {
        return true;
    }
    if (isTenant) {
        return costType !== EPaymentOrderType.OtherRecurring;
    }
    var currentPrice = contract.currentPrice, currentCostsCommon = contract.currentCostsCommon, currentCostsNonCommon = contract.currentCostsNonCommon, additionalCosts = contract.additionalCosts;
    var hasCommonCosts = currentCostsCommon && currentCostsCommon > 0;
    var hasNonCommonCosts = currentCostsNonCommon && currentCostsNonCommon > 0;
    var hasIndexationOpen = !internalModeEnabled &&
        (contract === null || contract === void 0 ? void 0 : contract.indexationStatus) === EContractIndexationStatus.Ready;
    var hasAdditionalCosts = (additionalCosts || []).length > 0;
    if (costType === EPaymentOrderType.RentAndCosts) {
        return ((!hasCommonCosts && !hasNonCommonCosts && !hasAdditionalCosts) ||
            hasIndexationOpen);
    }
    if (costType === EPaymentOrderType.Rent) {
        return !currentPrice || currentPrice === 0 || hasIndexationOpen;
    }
    if (costType === EPaymentOrderType.CommonCosts) {
        return !hasCommonCosts || hasIndexationOpen;
    }
    if (costType === EPaymentOrderType.NonCommonCosts) {
        return !hasNonCommonCosts;
    }
    return false;
};
export var isOnceCostDisabled = function (cost, isTenant) {
    if (isTenant === void 0) { isTenant = false; }
    if (isTenant) {
        return cost !== EPaymentOrderType.OtherOneoff;
    }
    return false;
};
var PAYMENT_TYPE_ICONS = {
    RENT_AND_COSTS: "realEstateActionBuildingStar",
    RENT: "forRentSign",
    COMMON_COSTS: "houseWrench",
    NON_COMMON_COSTS: "billSplit",
    MANAGEMENT_COSTS: "keys",
    RENTIO: "smartphone",
    OTHER_RECURRING: "billStack",
    RENT_DEPOSIT: "safe",
    LOCATION_DESCRIPTION: "houseNotes",
    BROKER_FEE: "broker",
    OTHER_ONEOFF: "billStack",
    ADDITIONAL_COSTS: "billStack",
};
export var getNavigationTitle = function (_a) {
    var paymentOrder = _a.paymentOrder;
    if (paymentOrder) {
        return getLocalizedText("payments.update.title");
    }
    return getLocalizedText("payments.create.title");
};
// The current management broker fee, expressed as a percentage of the rent
// (management costs amount / rent amount), matching the calculation shown on
// the payments overview (PaymentOrderRow). Returns undefined when it cannot be
// computed (no management costs order or no rent order to base it on).
//
// When `formData` is provided and the payment order being edited is the
// management costs order, its live form amount overrides the saved amount so
// the percentage updates as the user types (before saving). The rent amount is
// not editable in this form, and editing any other payment order type leaves
// the calculation untouched.
export var getManagementFeeBrokerPercentage = function (extraData, formData) {
    var paymentOrders = extraData.paymentOrders || [];
    var managementOrder = paymentOrders.find(function (paymentOrder) { return paymentOrder.type === EPaymentOrderType.ManagementCosts; });
    var rentOrder = paymentOrders.find(function (paymentOrder) { return paymentOrder.type === EPaymentOrderType.Rent; });
    if (!managementOrder || !rentOrder || !rentOrder.amount) {
        return undefined;
    }
    var editedType = (formData === null || formData === void 0 ? void 0 : formData[EField.TypeRecurring]) || (formData === null || formData === void 0 ? void 0 : formData[EField.TypeOnce]);
    var editedAmount = formData === null || formData === void 0 ? void 0 : formData[EField.Price];
    var managementAmount = editedType === EPaymentOrderType.ManagementCosts && editedAmount != null
        ? editedAmount
        : managementOrder.amount;
    return ((managementAmount / rentOrder.amount) * 100).toFixed(2);
};
// Localized info-banner text for the current management broker fee, or
// undefined when no percentage can be computed (banner should then be hidden).
export var getManagementFeeBrokerInfo = function (extraData, formData) {
    var percentage = getManagementFeeBrokerPercentage(extraData, formData);
    if (percentage === undefined) {
        return undefined;
    }
    return getLocalizedText("payment.payment_order.management_fee_broker.current_info", { amount: percentage });
};
// Concise "{{percentage}}% without VAT" value for the summary row, or undefined
// when no percentage can be computed (matches the payments overview label).
export var getManagementFeeBrokerSummaryValue = function (extraData, formData) {
    var percentage = getManagementFeeBrokerPercentage(extraData, formData);
    if (percentage === undefined) {
        return undefined;
    }
    return getLocalizedText("system.amount_without_vat", {
        amount: "".concat(percentage, "%"),
    });
};
var getPaymentOrderType = function (extraData) {
    var _a, _b;
    var paymentOrder = extraData.paymentOrder, activeContracts = extraData.activeContracts;
    if (!paymentOrder) {
        return;
    }
    var isRecurring = RECURRING_COSTS.includes(paymentOrder.type);
    var contract = activeContracts === null || activeContracts === void 0 ? void 0 : activeContracts.find(function (contract) { return contract.id === paymentOrder.contractId; });
    if (paymentOrder.type === EPaymentOrderType.OtherRecurring &&
        paymentOrder.otherDescription &&
        ((_a = contract === null || contract === void 0 ? void 0 : contract.additionalCosts) === null || _a === void 0 ? void 0 : _a.some(function (cost) { return cost.description === paymentOrder.otherDescription; }))) {
        var costIndex = (_b = contract === null || contract === void 0 ? void 0 : contract.additionalCosts) === null || _b === void 0 ? void 0 : _b.findIndex(function (cost) { return cost.description === paymentOrder.otherDescription; });
        return "".concat(EPaymentOrderType.AdditionalCosts, "_").concat(costIndex);
    }
    return isRecurring ? paymentOrder.type : undefined;
};
export var getInitialValues = function (extraData) {
    var _a, _b;
    var _c;
    var paymentOrder = extraData.paymentOrder, shouldUpdateOpenPaymentRequests = extraData.shouldUpdateOpenPaymentRequests, _d = extraData.activeContracts, activeContracts = _d === void 0 ? [] : _d, _e = extraData.endedContracts, endedContracts = _e === void 0 ? [] : _e, prefill = extraData.prefill;
    if (!paymentOrder) {
        var contracts = __spreadArray(__spreadArray([], activeContracts, true), endedContracts, true);
        var activeContract = void 0;
        var endedContract = void 0;
        // If there is only a single contract it can be prefilled
        if (contracts.length === 1) {
            if (activeContracts.length > 0) {
                activeContract = activeContracts[0];
            }
            else if (endedContracts.length > 0) {
                endedContract = endedContracts[0];
            }
        }
        var contract = activeContract || endedContract;
        var initialValues_1 = (_a = {},
            _a[EField.TypeRecurring] = undefined,
            _a[EField.TypeOnce] = undefined,
            _a[EField.ActiveContract] = activeContract === null || activeContract === void 0 ? void 0 : activeContract.id,
            _a[EField.EndedContract] = endedContract === null || endedContract === void 0 ? void 0 : endedContract.id,
            _a[EField.IncludeCommission] = "0",
            _a[EField.IncludeManagementFee] = "0",
            _a[EField.PaydateOffset] = "0",
            _a[EField.FirstPaymentReceivedDate] = NOW,
            _a[EField.FirstPaymentReceivedDateProRata] = NOW,
            _a[EField.PayoutReferenceType] = "DEFAULT",
            _a[EField.CommissionPayoutReferenceType] = "DEFAULT",
            _a[EField.ManagementFeePayoutReferenceType] = "DEFAULT",
            _a[EField.IsIndexable] = EContractCostsAreIndexable.No,
            _a[EField.LinkToContract] = true,
            _a);
        // Cost-settlement flow: a one-off cost. Force the type and pick the payer
        // so the type/frequency/contract/payer steps don't need to be asked.
        if (prefill === null || prefill === void 0 ? void 0 : prefill.costPayment) {
            initialValues_1[EField.TypeSelect] = ETypeSelect.Once;
            initialValues_1[EField.TypeOnce] = EPaymentOrderType.OtherOneoff;
            // Linked to the lease the embedder names (a settlement bills each
            // tenant for their own lease); otherwise no contract, and the created
            // payment order has no contractId.
            var prefillContract_1 = prefill.contractId
                ? contracts.find(function (_a) {
                    var id = _a.id;
                    return id === prefill.contractId;
                })
                : undefined;
            var isPrefillContractActive = !!prefillContract_1 &&
                activeContracts.some(function (_a) {
                    var id = _a.id;
                    return id === prefillContract_1.id;
                });
            initialValues_1[EField.ActiveContract] = isPrefillContractActive
                ? prefillContract_1 === null || prefillContract_1 === void 0 ? void 0 : prefillContract_1.id
                : undefined;
            initialValues_1[EField.EndedContract] =
                prefillContract_1 && !isPrefillContractActive
                    ? prefillContract_1.id
                    : undefined;
            initialValues_1[EField.WithoutContract] = prefillContract_1 ? undefined : "1";
            var payerId = (_c = prefill.payerId) !== null && _c !== void 0 ? _c : getCostPaymentPayerId({
                extraData: extraData,
                contract: prefillContract_1 !== null && prefillContract_1 !== void 0 ? prefillContract_1 : contract,
                isLinkedToContract: !!prefillContract_1,
            });
            // Someone the cost cannot bill is not preselected: the payer step is
            // then asked, and refuses them (see getValidate in step 4).
            if (payerId != null &&
                isAllowedCostPayer({
                    payerAccountIds: prefill.payerAccountIds,
                    accountId: payerId,
                })) {
                initialValues_1[EField.Payer] = [payerId];
            }
            // No beneficiary is picked for a cost payment: whoever paid the invoice
            // gets the tenant's money, the owner or the agency depending on the
            // case, so the broker chooses every time.
        }
        if (contract) {
            if (isAfter(contract.startDate, YESTERDAY)) {
                initialValues_1[EField.StartDate] = stripTime(max([NOW, contract.startDate]));
            }
        }
        // A cost payment should go out immediately. The date step is skipped for
        // cost payments, so force the pay date to today: the invitation then fires
        // now (pay date − offset is in the past) and the request is outstanding
        // right away, instead of inheriting the next-month default.
        if (prefill === null || prefill === void 0 ? void 0 : prefill.costPayment) {
            initialValues_1[EField.StartDate] = stripTime(NOW);
        }
        return initialValues_1;
    }
    var isRecurring = RECURRING_COSTS.includes(paymentOrder.type);
    var proRataAmount = paymentOrder.proRataAmount;
    // INFO: When repetition is delayed, add amount
    if (paymentOrder.isRepetitionDelayed && isRecurring) {
        proRataAmount = proRataAmount + paymentOrder.amount;
    }
    var priceVat = paymentOrder.vat || 0;
    var priceVatMapped = priceVat === 0
        ? paymentOrder.noVatType || ENoVatType.NotOnDeclaration
        : priceVat;
    var initialValues = (_b = {},
        _b[EField.TypeSelect] = isRecurring ? ETypeSelect.Recurring : ETypeSelect.Once,
        _b[EField.TypeRecurring] = getPaymentOrderType(extraData),
        _b[EField.TypeOnce] = isRecurring ? undefined : paymentOrder.type,
        _b[EField.ActiveContract] = paymentOrder.contractId,
        _b[EField.WithoutContract] = paymentOrder.contract ? undefined : "1",
        _b[EField.Price] = paymentOrder.amount,
        _b[EField.PriceVat] = priceVatMapped,
        _b[EField.PriceCommonCosts] = paymentOrder.amount,
        _b[EField.PriceCommonCostsVat] = priceVatMapped,
        _b[EField.PriceNonCommonCosts] = paymentOrder.amount,
        _b[EField.PriceNonCommonCostsVat] = priceVatMapped,
        _b[EField.StartDate] = stripTime(paymentOrder.startedAt),
        _b[EField.StartDateDropdown] = stripTime(paymentOrder.startedAt).toISOString(),
        _b[EField.PaydateOffset] = "".concat(paymentOrder.paydateOffset || 0),
        _b[EField.FirstPaymentReceivedDate] = paymentOrder.firstPayDateAt,
        _b[EField.FirstPaymentReceivedDateProRata] = paymentOrder.firstPayDateAt,
        _b[EField.ProRata] = paymentOrder.isRepetitionDelayed
            ? "EXTRA_MONTH"
            : "NORMAL",
        _b[EField.FirstPrice] = proRataAmount,
        _b[EField.Payer] = [paymentOrder.payerId],
        _b[EField.Payee] = [paymentOrder.payeeId],
        _b[EField.Iban] = paymentOrder.payeeBankaccountId,
        _b[EField.PayoutReferenceType] = paymentOrder.payoutReferenceCustom
            ? EPayoutReferenceType.Custom
            : EPayoutReferenceType.Default,
        _b[EField.PayoutReference] = paymentOrder.payoutReference,
        _b[EField.PayoutReferenceCustom] = paymentOrder.payoutReferenceCustom,
        _b[EField.UpdatePaymentRequests] = shouldUpdateOpenPaymentRequests
            ? "UPDATE_TRUE"
            : "UPDATE_FALSE",
        _b[EField.BookkeepingPeriodStart] = paymentOrder.bookkeepingPeriodStart,
        _b[EField.BookkeepingPeriodEnd] = paymentOrder.bookkeepingPeriodEnd,
        _b[EField.BookkeepingGeneralLedgerAccount] = paymentOrder.bookkeepingGeneralLedgerAccount,
        _b[EField.BookkeepingCostCenter] = paymentOrder.bookkeepingCostCenter,
        _b[EField.BookkeepingCostUnit] = paymentOrder.bookkeepingCostUnit,
        _b[EField.Document] = paymentOrder.documentId
            ? [paymentOrder.documentId]
            : undefined,
        _b[EField.LinkToContract] = true,
        _b);
    var paymentMethods = [];
    if (paymentOrder.paymentMethods.includes(EPaymentMethod.Sepa)) {
        paymentMethods.push(ECreatePaymentMethod.SepaOnce);
        if (paymentOrder.repetitionType !== EPaymentRepetitionType.Once) {
            paymentMethods.push(ECreatePaymentMethod.SepaRecurring);
        }
    }
    if (paymentOrder.paymentMethods.includes(EPaymentMethod.SepaOnce)) {
        paymentMethods.push(ECreatePaymentMethod.SepaOnce);
    }
    if (paymentOrder.paymentMethods.includes(EPaymentMethod.SepaRecurring)) {
        paymentMethods.push(ECreatePaymentMethod.SepaRecurring);
    }
    if (paymentOrder.paymentMethods.includes(EPaymentMethod.Bancontact) ||
        paymentOrder.paymentMethods.includes(EPaymentMethod.Payconiq) ||
        paymentOrder.paymentMethods.includes(EPaymentMethod.Ideal)) {
        paymentMethods.push(ECreatePaymentMethod.Online);
    }
    if (paymentOrder.paymentMethods.includes(EPaymentMethod.Ibanisation)) {
        paymentMethods.push(ECreatePaymentMethod.Ibanisation);
    }
    initialValues[EField.PaymentMethods] = uniq(paymentMethods);
    if (checkIfTypeIsOther(initialValues)) {
        initialValues[EField.OtherDescription] = paymentOrder.otherDescription;
    }
    if (shouldAskOwnerPaymentMethod(initialValues, extraData)) {
        initialValues[EField.OwnerPaymentMethod] = paymentOrder.ownerPaymentMethod;
    }
    if (isRecurring) {
        initialValues[EField.IsIndexable] = paymentOrder.isIndexable
            ? EContractCostsAreIndexable.Yes
            : EContractCostsAreIndexable.No;
    }
    return initialValues;
};
export var getSubtitle = function (payload) {
    var _a, _b;
    var item = payload.item, state = payload.state, contract = payload.contract, propertyId = payload.propertyId;
    var property = propertySelectorsV2.getDetail(state, (_b = (_a = contract === null || contract === void 0 ? void 0 : contract.propertyId) !== null && _a !== void 0 ? _a : propertyId) !== null && _b !== void 0 ? _b : -1);
    /*const property = propertySelectors.getProperty(
      state,
      contract ? contract.propertyId : -1,
    );*/
    if (!property) {
        return;
    }
    var accountId = item.accountId, contact = item.contact;
    var member = property.members.find(function (member) {
        if (accountId && accountId === member.account.id) {
            return true;
        }
        if (contact === null || contact === void 0 ? void 0 : contact.accountIds.includes(member.account.id)) {
            return true;
        }
        return false;
    });
    if (!member) {
        return;
    }
    return member.roles
        .filter(function (role) {
        return ![
            EPropertyMemberTypes.Admin,
            EPropertyMemberTypes.Signer,
            EPropertyMemberTypes.ContractMember,
            EPropertyMemberTypes.TaskMember,
        ].includes(role);
    })
        .map(function (role) { return getLocalizedText("role.".concat(role.toLowerCase())); })
        .join(", ");
};
export var customContactsValueSelector = function (payload) {
    var state = payload.state, propertyId = payload.propertyId, selectedAccountIds = payload.selectedAccountIds;
    var property = propertySelectorsV2.getDetail(state, propertyId);
    // const property = propertySelectors.getProperty(state, propertyId);
    if (!property) {
        return getLocalizedText("summary.view.contact");
    }
    return contactSelectInlineV2ValueSelector(state, selectedAccountIds, property.members.map(function (member) { return member.account; }));
};
export var customContactsSelector = function (payload) {
    var _a, _b, _c;
    var payerType = payload.payerType, state = payload.state, contract = payload.contract, type = payload.type, formData = payload.formData, costPayment = payload.costPayment, propertyId = payload.propertyId, payerAccountIds = payload.payerAccountIds;
    var property = propertySelectorsV2.getDetail(state, (_b = (_a = contract === null || contract === void 0 ? void 0 : contract.propertyId) !== null && _a !== void 0 ? _a : propertyId) !== null && _b !== void 0 ? _b : -1);
    if (!property) {
        return;
    }
    // Cost-settlement flow for one lease: its own tenants and parents pay
    // (a previous tenant is no longer a member of the property). A lease
    // without any falls back to the property's, as below.
    var contractPayers = costPayment && payerType === "payer"
        ? ((_c = contract === null || contract === void 0 ? void 0 : contract.members) !== null && _c !== void 0 ? _c : []).filter(function (member) {
            return COST_PAYER_CONTRACT_ROLES.some(function (role) {
                return member.roles.includes(role);
            }) &&
                isAllowedCostPayer({
                    payerAccountIds: payerAccountIds,
                    accountId: member.account.id,
                });
        })
        : [];
    if (contractPayers.length > 0) {
        return contractPayers.map(function (member) { return member.account; });
    }
    // Cost-settlement flow where the embedder names who may pay: just them.
    // The property lists everyone on its leases (as a contract member), the
    // previous tenants and a parent who pays the rent included.
    if (costPayment && payerType === "payer" && payerAccountIds) {
        return uniqBy(property.members.filter(function (member) {
            return payerAccountIds.includes(member.account.id);
        }), function (member) { return member.account.id; }).map(function (member) { return member.account; });
    }
    // Cost-settlement flow: derive options straight from the PROPERTY's members
    // (the payment is contractless) → tenants can pay, owners/managers receive.
    if (costPayment) {
        var wantedRoles_1 = payerType === "payer"
            ? [EPropertyMemberTypes.Tenant, EPropertyMemberTypes.Parent]
            : [
                EPropertyMemberTypes.Owner,
                EPropertyMemberTypes.Broker,
                EPropertyMemberTypes.FinancialManager,
                EPropertyMemberTypes.TechnicalManager,
                EPropertyMemberTypes.AdministrativeManager,
            ];
        return property.members
            .filter(function (member) { return member.roles.some(function (role) { return wantedRoles_1.includes(role); }); })
            .map(function (member) { return member.account; });
    }
    if (!contract) {
        return;
    }
    // An additional cost's type carries the cost's index ("ADDITIONAL_COSTS_0"), so it
    // matches no member of the switch below and every contract member would be filtered
    // out, leaving the user with "Other payer" alone. Offer the same parties
    // getDefaultPayerPayeeValue already picks its default from (DEV-2972).
    var isTypeAdditionalCost = checkIfTypeIsAdditionalCost(formData);
    var members = contract.members.filter(function (contractMember) {
        var roles = contractMember.roles;
        var propertyMember = property.members.find(function (randomPropertyMember) {
            return randomPropertyMember.account.id === contractMember.account.id;
        });
        var isTenant = roles.includes(EContractMemberTypes.Tenant) ||
            roles.includes(EContractMemberTypes.Parent);
        var isOwner = roles.includes(EContractMemberTypes.Owner);
        var isBrokerOrManager = (propertyMember === null || propertyMember === void 0 ? void 0 : propertyMember.roles.includes(EPropertyMemberTypes.Broker)) ||
            (propertyMember === null || propertyMember === void 0 ? void 0 : propertyMember.roles.includes(EPropertyMemberTypes.TechnicalManager)) ||
            (propertyMember === null || propertyMember === void 0 ? void 0 : propertyMember.roles.includes(EPropertyMemberTypes.FinancialManager));
        if (isTypeAdditionalCost) {
            return isTenant || isOwner || isBrokerOrManager;
        }
        switch (type) {
            case EPaymentOrderType.RentAndCosts:
            case EPaymentOrderType.Rent:
            case EPaymentOrderType.CommonCosts:
            case EPaymentOrderType.NonCommonCosts:
                if (payerType === "payer") {
                    return isTenant;
                }
                return isOwner;
            case EPaymentOrderType.ManagementCosts:
            case EPaymentOrderType.Rentio:
            case EPaymentOrderType.BrokerFee:
                if (payerType === "payer") {
                    return isOwner;
                }
                return isBrokerOrManager;
            case EPaymentOrderType.RentDeposit:
                if (payerType === "payer") {
                    return isTenant;
                }
                return isBrokerOrManager;
            case EPaymentOrderType.LocationDescription:
                if (payerType === "payer") {
                    return isTenant || isOwner;
                }
                return isBrokerOrManager;
            case EPaymentOrderType.OtherRecurring:
            case EPaymentOrderType.OtherOneoff:
                return isTenant || isOwner || isBrokerOrManager;
            default:
                return false;
        }
    });
    return members.map(function (member) { return member.account; });
};
export var getRepetitionDay = function (startDate, contract) {
    if (!startDate) {
        return 1;
    }
    var result = getDay(startDate);
    if (contract && contract.payDateStart === "calendar") {
        result = 1;
    }
    if (contract && contract.payDateStart === "contract") {
        result = getDay(contract.startDate);
    }
    return Math.min(28, result);
};
export var getPaymentTypeIcon = function (type) { return PAYMENT_TYPE_ICONS[type]; };
export var isNewContract = function (contract) {
    return isAfter(startOfDay(contract.startDate), startOfDay(YESTERDAY));
};
export var hasProRata = function (formData, contract) {
    if (!contract) {
        return false;
    }
    if (!isNewContract(contract)) {
        return false;
    }
    var startDate = contract.startDate, payDateStart = contract.payDateStart, _a = contract.repetitionType, repetitionType = _a === void 0 ? EContractPaymentRepetitionType.Monthly : _a;
    var selectedStartDate = getStartDate(formData);
    if (payDateStart === EContractPaydate.Calendar && selectedStartDate) {
        switch (repetitionType) {
            case EContractPaymentRepetitionType.Monthly: {
                return getDay(selectedStartDate) !== 1;
            }
            case EContractPaymentRepetitionType.Quarterly: {
                return (getDay(selectedStartDate) !== 1 ||
                    ![1, 4, 7, 10].includes(getMonth(selectedStartDate) + 1));
            }
            case EContractPaymentRepetitionType.Yearly: {
                return (getDay(selectedStartDate) !== 1 ||
                    getMonth(selectedStartDate) + 1 !== 1);
            }
            default:
                return false;
        }
    }
    if (payDateStart === EContractPaydate.Contract && selectedStartDate) {
        return getDay(selectedStartDate) !== getDay(startDate);
    }
    return false;
};
export var proRataForContract = function (contract, baseAmount) {
    if (contract.payDateStart === EContractPaydate.Contract) {
        return 0;
    }
    var periodDays = 0;
    var proRataDays = 0;
    switch (contract.repetitionType) {
        case EContractPaymentRepetitionType.Quarterly:
            periodDays = getProRataPeriodDays(FormData, contract);
            proRataDays = differenceInDays(endOfQuarter(contract.startDate), contract.startDate);
            break;
        case EContractPaymentRepetitionType.Yearly:
            periodDays = getProRataPeriodDays(FormData, contract);
            proRataDays = differenceInDays(endOfYear(contract.startDate), contract.startDate);
            break;
        case EContractPaymentRepetitionType.Monthly:
        default:
            periodDays = getProRataPeriodDays(FormData, contract);
            proRataDays = differenceInDays(endOfMonth(contract.startDate), contract.startDate);
            break;
    }
    if (proRataDays === 0) {
        return 0;
    }
    return (Math.round(baseAmount * (Math.min(proRataDays, periodDays) / periodDays) * 100) / 100);
};
export var getFirstPayDateAt = function (params) {
    var repetitionType = params.repetitionType, formData = params.formData, contract = params.contract;
    var userDate = formData[EField.FirstPaymentReceivedDate];
    var isRecurring = repetitionType !== EPaymentRepetitionType.Once;
    if (isRecurring && hasProRata(formData, contract)) {
        userDate = formData[EField.FirstPaymentReceivedDateProRata];
    }
    return new Date(userDate || getStartDate(formData));
};
export var isRepetitionDelayed = function (formData, contract) {
    // Only for recurring payments
    return !!formData[EField.TypeRecurring] &&
        // Only if pro-rata
        hasProRata(formData, contract) &&
        formData[EField.ProRata] === "EXTRA_MONTH";
};
export var getDefaultPayerPayeeValue = function (payload) {
    var key = payload.key, initialValues = payload.initialValues, defaultValues = payload.defaultValues, type = payload.type, contract = payload.contract, costPayment = payload.costPayment;
    if (key in defaultValues) {
        return defaultValues[key];
    }
    if (key in initialValues) {
        return initialValues[key];
    }
    if (!contract) {
        return;
    }
    var contractMembers = contract.members.filter(function (_a) {
        var roles = _a.roles;
        var isTenant = roles.includes(EContractMemberTypes.Tenant) ||
            roles.includes(EContractMemberTypes.Parent);
        var isOwner = roles.includes(EContractMemberTypes.Owner);
        var isBroker = roles.includes(EContractMemberTypes.Broker);
        // Cost-settlement flow: tenants pay; the beneficiary is chosen every
        // time (see getInitialValues), so no member is suggested for it.
        if (costPayment) {
            return key === EField.Payer ? isTenant : false;
        }
        var isTypeAdditionalCost = checkIfTypeIsAdditionalCost(defaultValues);
        if (isTypeAdditionalCost) {
            return isTenant || isOwner || isBroker;
        }
        switch (type) {
            case EPaymentOrderType.RentAndCosts:
            case EPaymentOrderType.Rent:
            case EPaymentOrderType.CommonCosts:
            case EPaymentOrderType.NonCommonCosts:
                if (key === EField.Payer) {
                    return isTenant;
                }
                return isOwner;
            case EPaymentOrderType.ManagementCosts:
            case EPaymentOrderType.Rentio:
            case EPaymentOrderType.BrokerFee:
                if (key === EField.Payer) {
                    return isOwner;
                }
                return isBroker;
            case EPaymentOrderType.RentDeposit:
                if (key === EField.Payer) {
                    return isTenant;
                }
                return isBroker;
            case EPaymentOrderType.LocationDescription:
                if (key === EField.Payer) {
                    return isTenant || isOwner;
                }
                return isBroker;
            case EPaymentOrderType.OtherRecurring:
            case EPaymentOrderType.OtherOneoff:
                return isTenant || isOwner || isBroker;
            default:
                return false;
        }
    });
    if (contractMembers.length === 1) {
        return [contractMembers[0].account.id];
    }
};
export var getActiveContracts = function (contracts) {
    return contracts.filter(function (contract) {
        return isBefore(endOfDay(new Date()), endOfDay(new Date(contract.stopDate)));
    });
};
export var getEndedContracts = function (contracts) {
    return contracts.filter(function (contract) {
        return isAfter(endOfDay(new Date()), endOfDay(new Date(contract.stopDate)));
    });
};
export var getRelatedContract = function (_a, formData) {
    var activeContracts = _a.activeContracts, endedContracts = _a.endedContracts, paymentOrder = _a.paymentOrder;
    var contracts = __spreadArray(__spreadArray([], (activeContracts || []), true), (endedContracts || []), true);
    var contractId = (paymentOrder === null || paymentOrder === void 0 ? void 0 : paymentOrder.contractId) ||
        formData[EField.ActiveContract] ||
        formData[EField.EndedContract];
    return contracts.find(function (contract) { return contract.id === contractId; });
};
export var getContract = function (contractId, _a) {
    var activeContracts = _a.activeContracts, endedContracts = _a.endedContracts;
    var contracts = __spreadArray(__spreadArray([], (activeContracts || []), true), (endedContracts || []), true);
    return contracts.find(function (contract) { return contract.id === contractId; });
};
export var contractHasRentPaymentOrder = function (extraData, formData) {
    var paymentOrders = extraData.paymentOrders;
    var contract = getRelatedContract(extraData, formData);
    if (!contract) {
        return false;
    }
    var rentPaymentOrder = paymentOrders === null || paymentOrders === void 0 ? void 0 : paymentOrders.find(function (paymentOrder) {
        return paymentOrder.type === EPaymentOrderType.Rent &&
            paymentOrder.contractId === contract.id;
    });
    return !!rentPaymentOrder;
};
export var getCreatePaymentCompanyFieldsSchema = function (contact) {
    return getAllRequiredCompanyFields({
        contact: contact,
        requiredFields: [
            EContactField.Address,
            EContactField.DateOfBirth,
            EContactField.Nationality,
            EContactField.PlaceOfBirth,
            EContactField.VATNumber,
        ],
        appendIdentityNumberToRequiredFields: false,
    });
};
export var getCreatePaymentContactFieldsSchema = function (contact) {
    return getAllRequiredContactFields({
        contact: contact,
        requiredFields: [
            EContactField.Address,
            EContactField.DateOfBirth,
            EContactField.Nationality,
            EContactField.PlaceOfBirth,
        ],
        appendIdentityNumberToRequiredFields: false,
    });
};
export var getBookkeepingSummaryForAdditionalCosts = function (bookkeepingSettings) {
    if (!(bookkeepingSettings === null || bookkeepingSettings === void 0 ? void 0 : bookkeepingSettings.length)) {
        return;
    }
    return bookkeepingSettings.map(function (settings, index) {
        var description = settings.description, bookkeepingGeneralLedgerAccount = settings.bookkeepingGeneralLedgerAccount, bookkeepingCostCenter = settings.bookkeepingCostCenter, bookkeepingCostUnit = settings.bookkeepingCostUnit;
        var ledgerAccountLabel = getLocalizedText("payment.payment_order.bookkeeping.general_ledger_account");
        var costCenterLabel = getLocalizedText("payment.payment_order.bookkeeping.cost_center");
        var costCenterUnitLabel = getLocalizedText("payment.payment_order.bookkeeping.cost_unit");
        var printableIndex = "".concat(index + 1, ". ");
        var values = [
            "".concat(ledgerAccountLabel, ": ").concat(bookkeepingGeneralLedgerAccount),
        ];
        if (bookkeepingCostCenter) {
            values.push("".concat(costCenterLabel, ": ").concat(bookkeepingCostCenter));
        }
        if (bookkeepingCostUnit) {
            values.push("".concat(costCenterUnitLabel, ": ").concat(bookkeepingCostUnit));
        }
        return (_jsxs("div", { children: [_jsx("div", { children: _jsx("b", { children: "".concat(printableIndex).concat(description, ":") }) }), "\u2003", values.join(" | ")] }));
    });
};
export var getAdditionalCostsSummary = function (additionalCosts) {
    if (!(additionalCosts === null || additionalCosts === void 0 ? void 0 : additionalCosts.length)) {
        return;
    }
    return additionalCosts.map(function (item, index) {
        // const priceType = item.priceType || EContractCostsPriceType.Fixed;
        var _a = item.additionalCostPrice, additionalCostPrice = _a === void 0 ? 0 : _a, _b = item.additionalCostPriceVat, additionalCostPriceVat = _b === void 0 ? 0 : _b, additionalCostAreIndexable = item.additionalCostAreIndexable, description = item.description;
        var price = formatCurrency(additionalCostPrice);
        // const vatLabel = getLocalizedText("contract_info.vat_amount.title");
        var vatValue = isNumber(additionalCostPriceVat)
            ? getLocalizedText("contract.item.vat", { value: "".concat(additionalCostPriceVat) }, additionalCostPriceVat)
            : getLocalizedText("contract.item.no_vat_type.".concat(additionalCostPriceVat).toLowerCase());
        var indexableLabel = getLocalizedText("contract_info.non_common_costs.are_indexable.label");
        var indexableValue = getLocalizedText("contract_info.costs.are_indexable.value.".concat(additionalCostAreIndexable === null || additionalCostAreIndexable === void 0 ? void 0 : additionalCostAreIndexable.toLowerCase()));
        // const priceTypeLabel = getLocalizedText(
        //   "contract_info.non_common_costs.price_type.label",
        // );
        // const priceTypeValue = getLocalizedText(
        //   `contract_info.costs.price_type.value.${priceType?.toLowerCase()}`,
        // );
        var printableIndex = "".concat(index + 1, ". ");
        return (_jsxs("div", { children: [_jsx("div", { children: _jsx("b", { children: "".concat(printableIndex).concat(description, ":") }) }), "\u2003", "".concat(price, " | ").concat(vatValue, " | ").concat(indexableLabel, ": ").concat(indexableValue)] }));
    });
};
